import { MxpModuleId } from './mxp.types';

const paths: Record<MxpModuleId, string> = {
  [MxpModuleId.ShoreExcursions]: '/activities/tours/shore-excursions',
  [MxpModuleId.Itinerary]: '/activities/tours/itinerary',
  [MxpModuleId.PortInfo]: '/itinerary',
  [MxpModuleId.MyCalendar]: '/my-calendar',
  [MxpModuleId.BoardActivities]: '/activities/dailyevents/board-activities',
  [MxpModuleId.Spa]: '/activities/spa/spa',
  [MxpModuleId.Fitness]: '/activities/spa/fitness',
  [MxpModuleId.HairDresser]: '/activities/spa/hair-dresser',
  [MxpModuleId.MakeUp]: '/activities/spa/permanent-makeup-parent',
  [MxpModuleId.Restaurants]: '/restaurants',
  [MxpModuleId.Packages]: '/packages',
  [MxpModuleId.Venues]: '/venues',
  [MxpModuleId.DeckPlan]: '/deck-plan',
  [MxpModuleId.OpeningHours]: '/opening-hours',
  [MxpModuleId.Announcements]: '/announcements',
  [MxpModuleId.Weather]: '/weather',
  [MxpModuleId.Favorites]: '/favorites',
  [MxpModuleId.Chat]: '/chat',
  [MxpModuleId.Messages]: '/messages',
};

export const isMxpUrl = (url: string | undefined): boolean => {
  if (typeof url === 'undefined') {
    return false;
  }

  return url.includes('mxp=');
};

export const createMxpUrlFromModule = (moduleId: MxpModuleId): string => {
  let path = getMxpModulePath(moduleId);

  return `?mxp=${encodeURIComponent(path)}`;
};

export const isMxpModuleId = (
  potentialModuleId: MxpModuleId | any,
): potentialModuleId is MxpModuleId => {
  if (typeof potentialModuleId !== 'string') {
    return false;
  }

  let moduleIds = Object.keys(paths);

  return moduleIds.includes(potentialModuleId);
};

export const getMxpModulePath = (moduleId: MxpModuleId): string => {
  return paths[<MxpModuleId>moduleId];
};

/**
 * Extracts the MXP path from a locationUri received from the navigationChanged event.
 * Example: "https://anbord.aws.test.meinschiff.com/mxp365tiles/itinerary?foo=bar" -> "itinerary"
 * Example: "https://anbord.aws.test.meinschiff.com/mxp365tiles/activities/spa/spa" -> "activities/spa/spa"
 * Example: "https://anbord.aws.test.meinschiff.com/mxp365tiles/mxp365tiles/itinerary" -> "itinerary"
 *
 * @param locationUri - The full URI from the navigationChanged event payload
 * @returns The extracted path segment that can be used to look up titles in Contentful
 */
export const extractPathFromLocationUri = (locationUri: string): string => {
  try {
    const url = new URL(locationUri);
    let pathname = url.pathname;

    // Extract everything after the LAST occurrence of /mxp365tiles/
    // This handles cases where the path contains duplicate /mxp365tiles/ segments
    const mxpTilesPrefix = '/mxp365tiles/';
    const lastIndex = pathname.lastIndexOf(mxpTilesPrefix);

    if (lastIndex === -1) {
      // Fallback: return the pathname without leading slash
      return pathname.startsWith('/') ? pathname.substring(1) : pathname;
    }

    // Extract the path after the last /mxp365tiles/
    return pathname.substring(lastIndex + mxpTilesPrefix.length);
  } catch (error) {
    // If URL parsing fails, try to extract manually using regex
    // Match the last occurrence of /mxp365tiles/ followed by path segments
    const pathMatch = locationUri.match(/\/mxp365tiles\/([^?#]*)(?:\?|#|$)/);
    if (pathMatch && pathMatch[1]) {
      // Handle potential duplicate mxp365tiles in the matched group
      const matchedPath = pathMatch[1];
      if (matchedPath.startsWith('mxp365tiles/')) {
        return matchedPath.substring('mxp365tiles/'.length);
      }
      return matchedPath;
    }

    // Last resort: return the locationUri as-is without query params
    return locationUri.split('?')[0].split('#')[0];
  }
};

/**
 * Finds a title for a given path by trying progressively shorter path prefixes.
 * This handles cases where the URL contains dynamic subpaths (e.g., dates, IDs)
 * that don't have specific titles in the collection.
 *
 * Example:
 * - Path: "activities/dailyevents/board-activities/2025-10-28"
 * - Titles: { "activities/dailyevents/board-activities": "Board Activities" }
 * - Result: "Board Activities" (matched by removing the date suffix)
 *
 * @param extractedPath - The path extracted from the locationUri
 * @param titles - The collection of title mappings from Contentful
 * @returns The matching title or undefined if no match found
 */
export const findTitleByPathPrefix = (
  extractedPath: string,
  titles: Record<string, string>,
): string | undefined => {
  // Helper function to try finding a title with or without leading slash
  const tryFindTitle = (path: string): string | undefined => {
    // Try an exact match
    if (titles[path]) {
      return titles[path];
    }

    // Try with the leading slash removed
    if (path.startsWith('/') && titles[path.substring(1)]) {
      return titles[path.substring(1)];
    }

    // Try with the leading slash added
    if (!path.startsWith('/') && titles['/' + path]) {
      return titles['/' + path];
    }

    return undefined;
  };

  // First, try the exact path
  let title = tryFindTitle(extractedPath);
  if (title) {
    return title;
  }

  // If not found, progressively remove path segments from the end
  const pathSegments = extractedPath.split('/').filter(segment => segment);

  // Start from one segment less than the full path, going down to a single segment
  for (let i = pathSegments.length - 1; i > 0; i--) {
    const shorterPath = pathSegments.slice(0, i).join('/');
    title = tryFindTitle(shorterPath);
    if (title) {
      return title;
    }
  }

  // No match found
  return undefined;
};
